// Preview/fixture mode — canned API responses for the standalone static
// preview deploy (console-pr-<N>.vibe-dev.vibe.mg).
//
// The console normally calls a SAME-ORIGIN control-plane API. A PR-preview is
// deployed as a plain MG-Vibes static app with NO backend, so every /api/v1/*
// call would otherwise hit the SPA fallback (index.html, 200 text/html) and
// the client would return `undefined` — leaving the home page on its
// "Couldn't load your apps" card and the user menu pulsing forever.
//
// When built with VITE_PREVIEW=1, `requestRaw()` in client.ts short-circuits
// to `resolveFixture()` below instead of issuing fetch(). This whole module is
// dead-code-eliminated from normal production builds (the call site is behind
// `if (import.meta.env.VITE_PREVIEW)`, which Vite statically replaces with a
// falsy literal when the flag is unset).
//
// Design goals:
//   1. Broad coverage — the endpoints that back the main routes (home, users,
//      groups, admin, app detail) return believable data so those pages render
//      populated rather than empty/erroring.
//   2. Graceful degradation — any endpoint NOT in the registry falls back to a
//      benign empty value so an un-fixtured call (or a button click that fires
//      a mutation) degrades to an empty/no-op state instead of throwing.

import type {
  ApiAdminOverviewResponse,
  ApiApp,
  ApiAppViewer,
  ApiDnsStatus,
  ApiGroup,
  ApiIdentity,
  ApiNamespace,
  ApiUserSummary,
} from './types';
import { resolveOrgFixture, groups as orgPreviewGroups } from './org-fixtures';

// Fixed timestamps so the built bundle is byte-stable across rebuilds (keeps
// the deploy dedupe/idempotency check meaningful). Relative-time labels on the
// page will read as frozen, which is fine for a preview.
const T_CREATED = 1_733_000_000_000; // ~2024-12
const T_DEPLOYED = 1_748_000_000_000; // ~2025-05

const PREVIEW_SUB = 'preview-user';
const PREVIEW_NS_ID = 'ns-preview-user';
const TEAM_NS_ID = 'ns-vibe-dev';

const ZERO_LLM_TOTALS = {
  calls: 0,
  errors: 0,
  prompt_tokens: 0,
  completion_tokens: 0,
  total_tokens: 0,
  cost_usd: 0,
};

const VIEWER_FULL: ApiAppViewer = {
  has_internal_access: true,
  can_manage: true,
  can_deploy: true,
  can_view_app_code: true,
  granted_to_me: false,
  can_share_externally: false,
};

const IDENTITY: ApiIdentity = {
  sub: PREVIEW_SUB,
  email: 'preview@vibe.mg',
  name: 'Preview User',
  user_class: 'member',
  user_class_reason: 'protected_admin',
  user_class_source: null,
  state: 'active',
  userNamespaceStub: 'preview-user',
  // platform_admin so the Admin nav link + /admin routes are reachable in the
  // preview. This identity is fictional and the preview has no backend, so it
  // grants no real access anywhere.
  platform_admin: true,
  memberships: [
    { namespace_id: PREVIEW_NS_ID, stub: 'preview-user', tier: 'user', roles: ['namespace_admin'] },
    { namespace_id: TEAM_NS_ID, stub: 'vibe-dev', tier: 'team', roles: ['deploy'] },
  ],
  groups: ['everyone'],
  principal_id: 'principal-preview-user',
};

const NAMESPACES: ApiNamespace[] = [
  {
    id: PREVIEW_NS_ID,
    tier: 'user',
    stub: 'preview-user',
    state: 'active',
    created_at: T_CREATED,
    created_by: PREVIEW_SUB,
    home_app_id: 'app-preview-console',
    guest_group: false,
    viewer: { can_add_members: false },
  },
  {
    id: TEAM_NS_ID,
    tier: 'team',
    stub: 'vibe-dev',
    display_name: 'Vibe Dev',
    description: 'Console PR previews live here.',
    state: 'active',
    created_at: T_CREATED,
    created_by: PREVIEW_SUB,
    owner_user_id: 'system',
    guest_group: false,
    viewer: { can_add_members: true },
  },
];

function makeApp(
  over: Partial<ApiApp> & Pick<ApiApp, 'id' | 'name' | 'namespace_id' | 'hostname'>,
): ApiApp {
  return {
    owner_user_id: PREVIEW_SUB,
    namespace_owned: false,
    kind: 'static',
    sharing: 'restricted',
    state: 'active',
    created_at: T_CREATED,
    last_deployed_at: T_DEPLOYED,
    current_deploy_id: 'dep-preview',
    resource_version: 'v1',
    resource_scope: 'app',
    write_protection: 'open',
    http_data_access: 'open',
    viewer: VIEWER_FULL,
    ...over,
  };
}

const APPS: ApiApp[] = [
  makeApp({
    id: 'app-preview-console',
    name: 'console-preview',
    namespace_id: PREVIEW_NS_ID,
    hostname: 'console-preview.preview-user.vibe.mg',
  }),
  makeApp({
    id: 'app-demo-dashboard',
    name: 'demo-dashboard',
    namespace_id: PREVIEW_NS_ID,
    hostname: 'demo-dashboard.preview-user.vibe.mg',
    sharing: 'internally-public',
  }),
  makeApp({
    id: 'app-hello-worker',
    name: 'hello-worker',
    namespace_id: PREVIEW_NS_ID,
    hostname: 'hello-worker.preview-user.vibe.mg',
    kind: 'worker',
  }),
  makeApp({
    id: 'app-vibe-docs',
    name: 'vibe-docs',
    namespace_id: TEAM_NS_ID,
    hostname: 'vibe-docs.vibe-dev.vibe.mg',
    owner_user_id: 'system',
    namespace_owned: true,
    sharing: 'internally-public',
  }),
  makeApp({
    id: 'app-team-tool',
    name: 'team-tool',
    namespace_id: TEAM_NS_ID,
    hostname: 'team-tool.vibe-dev.vibe.mg',
    owner_user_id: 'system',
    namespace_owned: true,
    kind: 'worker',
  }),
];

const USERS: ApiUserSummary[] = [
  {
    id: PREVIEW_SUB,
    email: 'preview@vibe.mg',
    display_name: 'Preview User',
    state: 'active',
    user_class: 'member',
    user_class_reason: 'protected_admin',
    user_class_source: null,
    user_namespace_stub: 'preview-user',
    active_apps_count: 3,
    archived_apps_count: 0,
  },
  {
    id: 'user-ada',
    email: 'ada@vibe.mg',
    display_name: 'Ada Lovelace',
    state: 'active',
    user_class: 'member',
    user_class_reason: 'manual_member',
    user_class_source: null,
    user_namespace_stub: 'ada-lovelace',
    active_apps_count: 1,
    archived_apps_count: 2,
  },
];

const GROUPS: ApiGroup[] = [
  {
    id: 'group-everyone',
    external_id: 'everyone',
    display_name: 'Everyone',
    created_at: T_CREATED,
    member_count: 2,
    source: 'virtual',
    group_kind: 'regular',
    guest_group: false,
    viewer: { can_manage: false, can_add_members: false, is_admin: false, is_member: true },
  },
  {
    id: 'group-vibe-dev',
    external_id: 'vibe-dev',
    display_name: 'Vibe Dev',
    created_at: T_CREATED,
    member_count: 1,
    source: 'team',
    group_kind: 'regular',
    guest_group: false,
    namespace: { id: TEAM_NS_ID, stub: 'vibe-dev', display_name: 'Vibe Dev' },
    viewer: { can_manage: false, can_add_members: false, is_admin: false, is_member: true },
  },
];

function dnsStatus(nsId: string, stub: string): ApiDnsStatus {
  return {
    namespace_id: nsId,
    namespace_stub: stub,
    fqdn: `${stub}.vibe.mg`,
    dns: 'present',
    tls: 'active',
    cert_status_raw: 'active',
    hosts: [`${stub}.vibe.mg`],
    source: 'cache',
    ready_since: T_CREATED,
  };
}

const ADMIN_OVERVIEW: ApiAdminOverviewResponse = {
  computed_at: T_DEPLOYED,
  overview: {
    users: { total: 2, active_recent_7d: 2, never_logged_in: 0 },
    apps: { active: APPS.filter((a) => a.state === 'active').length, archived: 0 },
    namespaces: { total: NAMESPACES.length, ready: NAMESPACES.length, pending: 0 },
    groups: { total: GROUPS.length, directory: 0, manual: 0, virtual: 1 },
    llm_aliases: { total: 0 },
    platform_admins: { total: 1 },
    reconciliation: { latest_run_at: null, findings_total: 0, findings_open: 0 },
    top_users_7d: [{ value: 'preview@vibe.mg', user_id: PREVIEW_SUB, total: 42 }],
  },
};

// ─── Registry ──────────────────────────────────────────────────────────────
// Each entry tests the (method, pathname) and builds the response body. First
// match wins. `path` here is the raw request path; we strip the query string
// before matching.

type Entry = {
  test: (method: string, pathname: string) => boolean;
  build: (pathname: string) => unknown;
};

const get =
  (re: RegExp): Entry['test'] =>
  (m, p) =>
    m === 'GET' && re.test(p);

const REGISTRY: Entry[] = [
  { test: get(/^\/api\/v1\/me$/), build: () => IDENTITY },
  {
    test: get(/^\/api\/v1\/limits$/),
    build: () => ({
      deploy: {
        max_file_bytes: 104857600,
        max_bundle_bytes: 524288000,
        max_zipped_bundle_bytes: 524288000,
        max_decompressed_bundle_bytes: 2147483648,
        max_file_count: 1000,
      },
      llm: { max_body_bytes: 1048576 },
      blob: { max_put_bytes: 26214400 },
    }),
  },
  { test: get(/^\/api\/v1\/namespaces$/), build: () => ({ namespaces: NAMESPACES }) },
  { test: get(/^\/api\/v1\/apps$/), build: () => ({ apps: APPS }) },
  {
    test: get(/^\/api\/v1\/namespaces\/[^/]+\/apps$/),
    build: (p) => {
      const nsId = p.split('/')[4];
      return { apps: APPS.filter((a) => a.namespace_id === nsId) };
    },
  },
  {
    test: get(/^\/api\/v1\/namespaces\/[^/]+\/dns-status$/),
    build: (p) => {
      const nsId = p.split('/')[4]!;
      const ns = NAMESPACES.find((n) => n.id === nsId);
      return dnsStatus(nsId, ns?.stub ?? 'preview-user');
    },
  },
  {
    test: get(/^\/api\/v1\/namespaces\/[^/]+$/),
    build: (p) => {
      const nsId = p.split('/')[4];
      return { namespace: NAMESPACES.find((n) => n.id === nsId) ?? NAMESPACES[0] };
    },
  },
  {
    test: get(/^\/api\/v1\/namespaces\/[^/]+\/members$/),
    build: () => ({ members: [], group_viewers: [] }),
  },
  {
    test: get(/^\/api\/v1\/apps\/[^/]+$/),
    build: (p) => {
      const id = p.split('/')[4];
      return { app: APPS.find((a) => a.id === id) ?? APPS[0] };
    },
  },
  { test: get(/^\/api\/v1\/apps\/[^/]+\/deploys$/), build: () => ({ deploys: [] }) },
  { test: get(/^\/api\/v1\/apps\/[^/]+\/grants$/), build: () => ({ grants: [] }) },
  { test: get(/^\/api\/v1\/apps\/[^/]+\/env$/), build: () => ({ env: [] }) },
  { test: get(/^\/api\/v1\/apps\/[^/]+\/audit$/), build: () => ({ audit: [] }) },
  { test: get(/^\/api\/v1\/users$/), build: () => ({ users: USERS }) },
  {
    test: get(/^\/api\/v1\/users\/[^/]+$/),
    build: (p) => {
      const id = p.split('/')[4];
      const u = USERS.find((x) => x.id === id) ?? USERS[0]!;
      return {
        user: {
          id: u.id,
          email: u.email,
          display_name: u.display_name,
          state: u.state,
          user_class: u.user_class,
          user_class_reason: u.user_class_reason,
          user_class_source: u.user_class_source,
          guest_enrollment_protection: null,
          user_namespace_stub: u.user_namespace_stub,
        },
        groups: [],
        memberships: [],
      };
    },
  },
  { test: get(/^\/api\/v1\/groups$/), build: () => ({ groups: [...GROUPS, ...orgPreviewGroups.map((group) => ({ ...group, created_at: T_CREATED, viewer: { can_manage: false, can_add_members: false, is_admin: false, is_member: false } }))] }) },
  {
    test: get(/^\/api\/v1\/groups\/[^/]+$/),
    build: (p) => {
      const id = decodeURIComponent(p.split('/')[4] ?? '');
      const org = orgPreviewGroups.find((x) => x.id === id || x.external_id === id);
      const g = org ? { ...org, created_at: T_CREATED, viewer: { can_manage: false, can_add_members: false, is_admin: false, is_member: false } } : GROUPS.find((x) => x.id === id || x.external_id === id) ?? GROUPS[0];
      return { group: g, members: [] };
    },
  },
  { test: get(/^\/api\/v1\/admin\/overview$/), build: () => ADMIN_OVERVIEW },
  {
    test: get(/^\/api\/v1\/admin\/platform_admins$/),
    build: () => ({
      platform_admins: [
        { user_id: PREVIEW_SUB, email: 'preview@vibe.mg', display_name: 'Preview User' },
      ],
    }),
  },
  { test: get(/^\/api\/v1\/admin\/virtual-groups$/), build: () => ({ virtual_groups: [] }) },
  // Preview has no evidence collector: report no current view so promotion
  // stays honestly unavailable rather than fabricating proof.
  {
    test: get(/^\/api\/v1\/admin\/guest-groups$/),
    build: () => ({
      groups: [],
      evidence: {
        revision: 0,
        view_id: null,
        collected_at: null,
        received_at: null,
        source: null,
        digest: null,
        fresh: false,
      },
    }),
  },
  {
    test: get(/^\/api\/v1\/admin\/llm-aliases$/),
    build: () => ({ aliases: [], default_alias: null }),
  },
  { test: get(/^\/api\/v1\/me\/tokens$/), build: () => ({ tokens: [] }) },
  { test: get(/^\/api\/v1\/me\/app-tokens$/), build: () => ({ tokens: [] }) },
  // LLM usage — HomeSidebar fetches /me/llm-usage on mount; the totals object
  // is read as `usage.totals.cost_usd` / `.calls` / `.errors`, so it must be a
  // real shape (the generic fallback's nested access would surface undefined).
  {
    test: get(/^\/api\/v1\/me\/llm-usage/),
    build: () => ({ range_days: 7, totals: ZERO_LLM_TOTALS, by_app: [], daily: [] }),
  },
  {
    test: get(/^\/api\/v1\/apps\/[^/]+\/llm-usage\/me/),
    build: () => ({ range_days: 7, totals: ZERO_LLM_TOTALS }),
  },
  {
    test: get(/^\/api\/v1\/apps\/[^/]+\/llm-usage/),
    build: () => ({ range_days: 7, totals: ZERO_LLM_TOTALS, by_user: [], by_model: [], daily: [] }),
  },
];

// Graceful fallback for any endpoint not in the registry. Returns a recursive,
// iterable empty value: property access yields another empty value (so deep
// reads like `x.totals.errors` never throw "reading … of undefined"), it
// behaves as an empty array (`.length === 0`, `.map`/`for..of` yield nothing),
// and it stays non-thenable so `await` resolves it as-is. This is the "fail
// gracefully where possible" backstop — an un-fixtured route renders its own
// empty state, and a mutation's return reads as empty rather than crashing.
function emptyValue(): unknown {
  const target: unknown[] = [];
  return new Proxy(target, {
    get(t, prop) {
      if (prop === 'then') return undefined; // never look thenable to await
      // Preserve real array behavior (length, iterator, map, forEach, …) so
      // callers that expect a list get a well-behaved empty one.
      const v = (t as unknown as Record<string | symbol, unknown>)[prop];
      if (prop === Symbol.iterator || typeof v === 'function' || prop === 'length') return v;
      if (prop in t) return v;
      // Any other property → another empty value, so nested access chains.
      return emptyValue();
    },
  });
}

export function resolveFixture(method: string, path: string, body?: unknown): unknown {
  const pathname = path.split('?')[0] ?? path;
  if (pathname.startsWith('/api/v1/org/') || pathname.startsWith('/api/v1/admin/org/')) return resolveOrgFixture(method, path, body);
  if (method === 'PATCH' && /^\/api\/v1\/apps\/[^/]+$/.test(pathname) && body && typeof body === 'object' && 'org_directory_enabled' in body && typeof body.org_directory_enabled === 'boolean') {
    const app = APPS.find((a) => a.id === pathname.split('/')[4]);
    if (!app) throw new Error('Synthetic preview app not found.');
    app.org_directory_enabled = body.org_directory_enabled;
    return { app };
  }
  for (const entry of REGISTRY) {
    if (entry.test(method, pathname)) return entry.build(pathname);
  }
  return emptyValue();
}
